import React, { useContext, useReducer, createContext } from 'react'
import localStorageService, { keys } from './services/LocalStorageService'
import userService from './services/UserService'
import plotService from './services/PlotService'
import seasonService from './services/SeasonService'
import projectService from './services/ProjectService'
import uploadService from './services/UploadService'
import { excrementScales } from './utils'
import moment from 'moment'

const Context = createContext();

const ACTION_LOGIN = "login"
const ACTION_RELOAD = "reload"
const ACTION_SET_PLOTS = "set_plots"
const ACTION_SET_SHORTLIST = "set_shortlist"
const ACTION_SELECT_YEAR = "select_year"
const ACTION_SELECT_PLOT = "select_plot"
const ACTION_SET_APPVERSION = "set_appversion"
const ACTION_LOGOUT = "logout"
const ACTION_UNSAVED = "unsaved"
const ACTION_GET_NEXTID = "get_nextid"

const initialState = {
    user: null,
    settings: null,
    plots: [],
    years: [],
    year: null,
    plot: null,
    nextId: 1,
    unsaved: [],
    shortlist: {},
    appversion: '',
}

const reducer = (state, action) => {
    switch (action.type) {

        case ACTION_LOGIN:
            return {
                ...state,
                user: action.payload.user,
                settings: action.payload.settings,
                plots: action.payload.plots,
                years: action.payload.years,
                plot: action.payload.plot,
                year: action.payload.year,
                unsaved: action.payload.unsaved,
            }

        case ACTION_RELOAD:
            return {
                ...state,
                settings: action.payload.settings,
                plots: action.payload.plots,
                years: action.payload.years,
                plot: action.payload.plot,
                year: action.payload.year,
            }

        case ACTION_SET_PLOTS:
            return { ...state, plots: action.payload.plots }
        
        case ACTION_SET_SHORTLIST:
            return { ...state, shortlist: action.payload.shortlist }

        case ACTION_SELECT_YEAR:
            return { ...state, year: action.payload.year }

        case ACTION_SELECT_PLOT:
            return { ...state, plot: action.payload.plot }

        case ACTION_SET_APPVERSION:
            return { ...state, appversion: action.payload.appversion }

        case ACTION_UNSAVED:
            return { ...state, unsaved: action.payload.unsaved }

        case ACTION_GET_NEXTID:
            return { ...state, nextId: state.nextId + 1 }

        case ACTION_LOGOUT:
            return { ...initialState }

        default:
            throw new Error(`dispatch: unknown action type '${action.type}'`)
    }
}

// StoreProvider
const Store = ({ children }) => {
    const user = localStorageService.getItem(keys.USER)
    let lastState = { ...initialState };
    if (user && user.loggedin) {
        lastState.user = user;
        lastState.settings = localStorageService.getItem(keys.SETTINGS);
        lastState.plots = localStorageService.getItem(keys.PLOTS);
        lastState.years = localStorageService.getItem(keys.YEARS);
        lastState.year = localStorageService.getItem(keys.YEAR);
        lastState.plot = localStorageService.getItem(keys.PLOT);
        lastState.nextId = localStorageService.getItem(keys.NEXTID) ?? 1;
        lastState.unsaved = localStorageService.getItem(keys.UNSAVED);
        lastState.shortlist = localStorageService.getItem(keys.SHORTLIST) ?? {};
    }

    const [state, dispatch] = useReducer(reducer, lastState)
    return (
        <Context.Provider value={[state, dispatch]}>
            {children}
        </Context.Provider>
    )
}

export const useStore = () => {
    const [state, dispatch] = useContext(Context)

    const nextId = () => {
        const id = state.nextId
        dispatch({ type: ACTION_GET_NEXTID })
        localStorageService.setItem(keys.NEXTID, id + 1)
        return id
    }

    const createVisit = (newPlot) => {
        let timeStart = moment()
        
        let plot = null
        switch (parseInt(process.env.REACT_APP_PROJECTID)) {
            case 57:
                plot = (state.plot && state.plot !== -1 ? state.plots.find(p => p.id === state.plot).children[0] : state.plots[0].children[0])
                break
            default:
                plot = newPlot||(state.plot && state.plot !== -1 ? state.plots.find(p => p.id === state.plot) : state.plots[0])
                break
        }
        let sections= []
        const createSection= (s) => {
            return {    description:s.description, 
                        observations:[],
                        uploads: [],
                        statusId: 3,
                        extraInfo: {
                            opmerking_obs: '',
                            gebruik_fuiken: 'nee'
                        }
                    }
        }
        if (parseInt(process.env.REACT_APP_PROJECTID)===46) {
            sections= plot.children.filter(s => s.statusId === 3).map(s => createSection(s))
        }
        const visit = {
            id: -nextId(),
            plot: plot,
            description: timeStart.format('DD-MM-YYYY'),
            timeStart: timeStart.format('YYYY-MM-DDTHH:mm:00'),
            timeStop: '',
            extraInfo: {},
            observations: [],
            uploads: [],
            sections: sections,
            statusId: 3,
            roles: [{ 
                id: -1, 
                statusId: 1, 
                roleTypeId: parseInt(process.env.REACT_APP_PROJECTID)===61 ? 121 : 5,
                person: { id: state.user.id }
            }],
            visitRights: 2,
            abundanceTotal: 0
        }
        state.settings.visitFields.forEach(field => {
            if (!field.historic && field.defaultValue !== null) visit.extraInfo[field.name]= field.defaultValue
        })
        return visit
    }

    const createObservation = () => {
        const obs = {
            id: -nextId(),
            delstaId: 0,
            location: null,
            description: "",
            uploads: [],
            abundance: null,
            extraInfo: {}
        }
        switch (parseInt(process.env.REACT_APP_PROJECTID)) {
            case 58:
                obs.taxLisTaxId = ""
                obs.abundance = 1
                break
            default:
                break
        }
        obs.index = Math.round(Date.now() / 1000)
        return obs
    }

    const featurePopup = (details) => {
        var popup = `<b>${details.observation.description}</b><hr />
        datum: ${details.visit.description}`
        if (details.visit.plot) popup += `</br>telgebied: ${details.visit.plot.description}`
        if (parseInt(process.env.REACT_APP_PROJECTID) === 56) {
            const info = details.observation.extraInfo
            if (info.locatietype) popup += `</br>locatietype: ${info.locatietype}`
            if (info.onderwerp) popup += `</br>wat gevonden: ${info.onderwerp}`
            if (info.zoekmethode) popup += `</br>hoe waargenomen: ${info.zoekmethode}`
        }

        if (parseInt(process.env.REACT_APP_PROJECTID) === 57 ) {
            var taxaItem= null
            state.settings.taxaLists.forEach((list) => {
                taxaItem= taxaItem||list.taxa.find(item => item.id === parseInt(details.observation.taxLisTaxId))
            })
            if (taxaItem && taxaItem.info.scale === 'amount/bat_excrements') {
                const scale= excrementScales().find(scale => scale.code === details.observation.abundance)
                popup += `</br>mest: ${scale?scale.description:details.observation.abundance}`
            } else {
                popup += `</br>aantal: ${details.observation.abundance}`
            }
        }

        if (parseInt(process.env.REACT_APP_PROJECTID) === 58) {
            popup += `</br>aantal: ${details.observation.abundance}`
        }

        if (parseInt(process.env.REACT_APP_PROJECTID) === 62 ) {
            popup += `</br>aantal: ${details.observation.abundance}`
        }
        [...details.visit.uploads, ...details.observation.uploads].forEach(upl => {
            popup += `<hr />`
            popup += `<b>${upl.origname}</b></br>`
            if (upl.author) popup += `(${upl.author})</br>`
            if (upl.remarks) popup += `${upl.remarks}</br>`
            popup += `<img src=${uploadService.getThumbUrl(upl.id)} />`
        })
        return popup
    }

    const createUpload = () => {
        const upl = {
            id: -nextId(),
            delstaId: 0,
            author: "",
            origname: "",
            publish: 0,
            dirty: 1,
            remarks: null
        }
        return upl
    }


    const setYear = (year) => {
        dispatch({ type: ACTION_SELECT_YEAR, payload: { year } })
        localStorageService.setItem(keys.YEAR, year)
    }

    const setPlot = (plot, localstorage= true) => {
        dispatch({ type: ACTION_SELECT_PLOT, payload: { plot } })
        if (localstorage) {
            localStorageService.setItem(keys.PLOT, plot)
        }
    }

    const setShortlist = (shortlist) => {
        dispatch({ type: ACTION_SET_SHORTLIST, payload: { shortlist } })
        localStorageService.setItem(keys.SHORTLIST, shortlist)
    }

    const setAppversion = (appversion) => {
        dispatch({ type: ACTION_SET_APPVERSION, payload: { appversion } })
    }

    // add visit or observation to unsaved list
    const updateUnsaved = (obj) => {
        obj.offline = true
        const unsaved = state.unsaved.filter(item => item.id !== obj.id);
        unsaved.push(obj)
        dispatch({ type: ACTION_UNSAVED, payload: { unsaved: unsaved } })
        localStorageService.setItem(keys.UNSAVED, unsaved.map(item => filterUploads(item)))
    }

    // remove visit from unsaved visit list
    const deleteUnsaved = (obj) => {
        const unsaved = state.unsaved.filter(item => item.id !== obj.id);
        dispatch({ type: ACTION_UNSAVED, payload: { unsaved } })
        localStorageService.setItem(keys.UNSAVED, unsaved.map(item => filterUploads(item)))
    }

    const login = async (username, password) => {
        const res = await userService.authenticate(username, password)
        const user = res.data
        user.loggedin = false
        localStorageService.setItem(keys.USER, user)
        localStorageService.setItem(keys.DEVICE, user.deviceId)
        const [settingsRes, plotsRes, yearsRes] = await Promise.all([projectService.getSettings(), plotService.getPlots(), seasonService.getYears()])
        user.loggedin = true
        const settings = settingsRes.data
        const plots = plotsRes.data.sort((a, b) => a.description > b.description ? 1 : -1)
        const years = yearsRes.data.sort((a, b) => a.description > b.description ? -1 : 1)
        const year = (years.length ? years[0].id : '')
        const plot = -1;
        const unsaved = []

        localStorageService.setItem(keys.SETTINGS, settings)
        localStorageService.setItem(keys.PLOTS, plots)
        localStorageService.setItem(keys.YEARS, years)
        localStorageService.setItem(keys.USER, user)
        localStorageService.setItem(keys.YEAR, year)
        localStorageService.setItem(keys.PLOT, plot)
        localStorageService.setItem(keys.UNSAVED, unsaved)

        dispatch({ type: ACTION_LOGIN, payload: { settings, plots, years, user, plot, year, unsaved } })
    }

    const register = async (user) => {
        await userService.register(user)
    }

    const reload = async () => {
        const [settingsRes, plotsRes, yearsRes] = await Promise.all([projectService.getSettings(), plotService.getPlots(), seasonService.getYears()])
        const settings = settingsRes.data
        const plots = plotsRes.data.sort((a, b) => a.description > b.description ? 1 : -1)
        const years = yearsRes.data.sort((a, b) => a.description > b.description ? -1 : 1)
        const year= years.find(item => item.id === state.year)?state.year:years.find(item => moment().isSameOrAfter(moment(item.timeStart)))?.id || ''
        const plot= plots.find(item => item.id === state.plot)?.id || -1;

        localStorageService.setItem(keys.SETTINGS, settings)
        localStorageService.setItem(keys.PLOTS, plots)
        localStorageService.setItem(keys.YEARS, years)
        localStorageService.setItem(keys.YEAR, year)
        localStorageService.setItem(keys.PLOT, plot)

        dispatch({ type: ACTION_RELOAD, payload: { settings, plots, years, plot, year } })
    }

    const setPlots = async () => {
        const response = await plotService.getPlots()
        const plots = response.data
        const plot = (plots.some(p => state.plot === p.id) ? state.plot : -1)

        localStorageService.setItem(keys.PLOTS, plots)

        dispatch({ type: ACTION_SET_PLOTS, payload: { plots } })
        if (plot !== state.plot) {
            setPlot(plot)
        }
    }

    const addPlot = (plot) => {
        const plots= [...state.plots, plot]
        dispatch({ type: ACTION_SET_PLOTS, payload: { plots } })
    }

    const logoff = () => {
        dispatch({ type: ACTION_LOGOUT })
        Object.keys(keys).filter(key => key !== 'DEVICE').forEach(key => {
            localStorageService.removeItem(keys[key])
        })
    }

    const saveState = (state) => {
        if (state.visit) localStorageService.setItem(keys.VISIT, filterUploads(state.visit))
        if (state.values) localStorageService.setItem(keys.VALUES, filterUploads(state.values))
    }

    const removeState = () => {
        localStorageService.removeItem(keys.VISIT)
        localStorageService.removeItem(keys.VALUES)
    }

    const filterUploads = (obj) => {
        if ('observations' in obj) {
            const filtered = { ...obj, observations: [], observation: null }
            obj.observations.forEach(o => {
                const observation = { ...o }
                observation.uploads = o.uploads.filter(u => u.id > 0)
                filtered.observations.push(observation)
            })
            return filtered
        } else {
            return { ...obj, uploads: obj.uploads.filter(u => u.id > 0) }
        }
    }


    return {
        nextId,
        createVisit,
        createObservation,
        createUpload,
        updateUnsaved,
        deleteUnsaved,
        featurePopup,
        storeContext: state,
        user: state.user,
        plots: state.plots,
        years: state.years,
        settings: state.settings,
        plot: state.plot,
        year: state.year,
        unsaved: state.unsaved,
        shortlist: state.shortlist,
        setPlot,
        addPlot,
        setShortlist,
        setYear,
        setAppversion,
        appversion: state.appversion,
        login,
        register,
        logoff,
        reload,
        setPlots,
        saveState,
        removeState,
    }
}

export default Store;
